import { useState, useEffect } from 'react';
import { XMarkIcon, BellIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { toast } from '../../ui/Toast';
import type { TemplateNotification, TemplateShare } from '../../../types/stepWork';
import {
  getNotifications,
  getPendingShares,
  acceptShare,
  declineShare,
} from '../../../services/firebase/templateSharing';
import { TemplatePreviewModal } from './TemplatePreviewModal';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onRefresh?: () => void;
}

export function NotificationsPanel({
  isOpen,
  onClose,
  userId,
  onRefresh,
}: NotificationsPanelProps) {
  const [notifications, setNotifications] = useState<TemplateNotification[]>([]);
  const [shares, setShares] = useState<Map<string, TemplateShare>>(new Map());
  const [loading, setLoading] = useState(true);
  const [selectedShare, setSelectedShare] = useState<TemplateShare | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, userId]);

  async function loadNotifications() {
    try {
      setLoading(true);
      const [notifs, pendingShares] = await Promise.all([
        getNotifications(userId),
        getPendingShares(userId),
      ]);

      // Create map of shares by ID
      const sharesMap = new Map<string, TemplateShare>();
      pendingShares.forEach(share => {
        sharesMap.set(share.id, share);
      });

      setNotifications(notifs.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis()));
      setShares(sharesMap);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleAccept(shareId: string) {
    try {
      const result = await acceptShare(shareId, userId);
      if (result.success) {
        await loadNotifications();
        if (onRefresh) onRefresh();
      } else {
        toast.error('Failed to accept template', result.error);
      }
    } catch (error) {
      console.error('Error accepting share:', error);
      toast.error('Failed to accept template', 'Please try again.');
    }
  }

  async function handleDecline(shareId: string) {
    try {
      const result = await declineShare(shareId, userId);
      if (result.success) {
        await loadNotifications();
      } else {
        toast.error('Failed to decline template', result.error);
      }
    } catch (error) {
      console.error('Error declining share:', error);
      toast.error('Failed to decline template', 'Please try again.');
    }
  }

  function handlePreview(shareId: string) {
    const share = shares.get(shareId);
    if (share) {
      setSelectedShare(share);
      setShowPreview(true);
    }
  }

  if (!isOpen) return null;

  const unreadNotifications = notifications.filter(n => !n.read);
  const readNotifications = notifications.filter(n => n.read);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
        <div className="bg-white dark:bg-navy-800 rounded-t-xl sm:rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-navy-700">
            <h2 className="text-xl font-semibold text-navy-900 dark:text-navy-100">
              Notifications
            </h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-12">
                <BellIcon className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                <p className="text-lg font-medium text-navy-900 dark:text-navy-100 mb-2">
                  No Notifications
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  You'll be notified when sponsors share templates with you
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Unread Notifications */}
                {unreadNotifications.map(notification => {
                  const share = shares.get(notification.shareId);
                  const isPending = share?.status === 'pending';

                  return (
                    <div
                      key={notification.id}
                      className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-4 border-2 border-teal-200 dark:border-teal-800"
                    >
                      <div className="flex items-start gap-3">
                        <BellIcon className="w-5 h-5 text-teal-600 dark:text-teal-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-navy-900 dark:text-navy-100 mb-1">
                            {notification.fromUserName} shared a template
                          </p>
                          <p className="text-sm text-slate-700 dark:text-slate-300">
                            {notification.templateName} ({notification.fellowship})
                          </p>
                          <div className="flex gap-2 mt-3">
                            {isPending ? (
                              <>
                                <button
                                  onClick={() => handlePreview(notification.shareId)}
                                  className="text-xs px-3 py-1.5 rounded-lg bg-white dark:bg-navy-700 border border-teal-200 dark:border-teal-700 text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-colors"
                                >
                                  Preview
                                </button>
                                <button
                                  onClick={() => handleAccept(notification.shareId)}
                                  className="text-xs px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600 text-white transition-colors"
                                >
                                  Accept
                                </button>
                                <button
                                  onClick={() => handleDecline(notification.shareId)}
                                  className="text-xs px-3 py-1.5 rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-colors"
                                >
                                  Decline
                                </button>
                              </>
                            ) : (
                              <span className="text-xs text-slate-500 dark:text-slate-500 italic">
                                {share?.status || 'No longer available'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Read Notifications */}
                {readNotifications.map(notification => (
                  <div
                    key={notification.id}
                    className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700 opacity-75"
                  >
                    <div className="flex items-start gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-navy-900 dark:text-navy-100 mb-1">
                          {notification.fromUserName} shared a template
                        </p>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {notification.templateName} ({notification.fellowship})
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                          Responded
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Template Preview Modal */}
      {selectedShare && (
        <TemplatePreviewModal
          isOpen={showPreview}
          onClose={() => {
            setShowPreview(false);
            setSelectedShare(null);
          }}
          share={selectedShare}
          onAccept={async (shareId) => {
            await handleAccept(shareId);
            setShowPreview(false);
            setSelectedShare(null);
          }}
          onDecline={async (shareId) => {
            await handleDecline(shareId);
            setShowPreview(false);
            setSelectedShare(null);
          }}
        />
      )}
    </>
  );
}
